import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import { Coins, Loader2, RefreshCcw } from "lucide-react";

const sourceOptions = ["all", "purchase", "chat", "video_call", "live_stream", "gift", "refund", "admin", "other"];
const flowOptions = ["all", "spent", "get"];

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

export default function CoinRecords() {
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({
    totalCoins: 0,
    purchaseCoins: 0,
    spentCoins: 0,
    redeemedCoins: 0,
    paymentRecords: 0,
  });
  const [source, setSource] = useState("all");
  const [flow, setFlow] = useState("all");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  const fetchRecords = useCallback(async (targetPage = page) => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page: targetPage,
        limit: 20,
        source,
        flow,
      };

      if (userId.trim()) params.userId = userId.trim();

      const { data } = await API.get("/admin/users/transactions/history", { params });
      if (!data?.success) throw new Error(data?.message || "Failed to load coin records");

      setRecords(Array.isArray(data.transactions) ? data.transactions : []);
      setStats({
        totalCoins: Number(data?.stats?.totalCoins || 0),
        purchaseCoins: Number(data?.stats?.purchaseCoins || 0),
        spentCoins: Number(data?.stats?.spentCoins || 0),
        redeemedCoins: Number(data?.stats?.redeemedCoins || 0),
        paymentRecords: Number(data?.stats?.paymentRecords || 0),
      });
      setTotalPages(Number(data.totalPages || 1));
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load coin records");
    } finally {
      setLoading(false);
    }
  }, [flow, source, userId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [source, flow, userId]);

  // Fetch records whenever page or filter definitions change
  useEffect(() => {
    fetchRecords(page);
  }, [page, fetchRecords]);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === records.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(records.map((r) => r._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: "coinRecords",
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        fetchRecords(page);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  const cards = useMemo(() => [
    { label: "All Coin Records", value: total },
    { label: "Purchase Coins", value: formatNumber(stats.purchaseCoins) },
    { label: "Redeem/Get Coins", value: formatNumber(stats.redeemedCoins) },
    { label: "Spent Coins", value: formatNumber(stats.spentCoins) },
    { label: "Payment Details Records", value: formatNumber(stats.paymentRecords) },
  ], [stats, total]);

  return (
    <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Coin And Payment Records</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">All coin purchase, redeem/get, spent and payment detail records</p>
        </div>
        <button
          onClick={() => fetchRecords(page)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-slate-700"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                <p className="text-xl font-bold text-gray-800 dark:text-white mt-1">{card.value}</p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-500/10 flex items-center justify-center">
                <Coins className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-300">
            Source
            <select
              value={source}
              onChange={(e) => {
                setPage(1);
                setSource(e.target.value);
              }}
              className="mt-1 w-full px-3 py-2 rounded-lg dark:bg-slate-800 border border-gray-300 dark:border-slate-600 bg-transparent"
            >
              {sourceOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-600 dark:text-gray-300">
            Flow
            <select
              value={flow}
              onChange={(e) => {
                setPage(1);
                setFlow(e.target.value);
              }}
              className="mt-1 w-full px-3 py-2 rounded-lg dark:bg-slate-800 border border-gray-300 dark:border-slate-600 bg-transparent"
            >
              {flowOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-600 dark:text-gray-300 md:col-span-2">
            User ID (optional)
            <input
              value={userId}
              onChange={(e) => {
                setPage(1);
                setUserId(e.target.value);
              }}
              placeholder="Filter by specific user id"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-transparent"
            />
          </label>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead className="bg-gray-50 dark:bg-slate-700/50">
              <tr className="text-xs uppercase text-gray-500 dark:text-gray-300">
                <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={records.length > 0 && selectedIds.length === records.length}
                    onChange={handleToggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                  />
                </th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Type</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Source</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Coins</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">From</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">To</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Status</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Payment Ref</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Meta</th>
                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading records...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-10 text-center text-gray-500">No records found</td>
                </tr>
              ) : records.map((item) => {
                const isSelected = selectedIds.includes(item._id);
                return (
                <tr key={item._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                  <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelectRow(item._id)}
                      className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-gray-800 dark:text-gray-200">{item.type || "-"}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-gray-700 dark:text-gray-300">{item.source || "-"}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 font-semibold text-amber-600">{formatNumber(item.amount)}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-gray-700 dark:text-gray-300">{item.fromUser?.name || item.fromUser?.username || "System"}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-gray-700 dark:text-gray-300">{item.toUser?.name || item.toUser?.username || "-"}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${item.status === "success" ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300" : "bg-gray-200 text-gray-700 dark:bg-slate-700 dark:text-gray-300"}`}>
                      {item.status || "-"}
                    </span>
                  </td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-xs text-gray-500">{item.idempotencyKey || "-"}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-xs text-gray-500">{item.meta?.note || "-"}</td>
                  <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-xs text-gray-500">{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <button
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={page <= 1 || loading}
          className="px-3 py-1.5 rounded border border-gray-300 dark:border-slate-600 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
        >
          Prev
        </button>
        <span>Page {page} / {totalPages}</span>
        <button
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={page >= totalPages || loading}
          className="px-3 py-1.5 rounded border border-gray-300 dark:border-slate-600 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
        >
          Next
        </button>
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={records.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName="coin records"
      />
    </div>
  );
}
